import { useEffect, useState } from "react";
import type { StripePlanPrice, StripePricesResponse } from "../api/checkout";
import type { CheckoutProvider } from "./checkoutProvider";
import type { PaymentPlanOption } from "./types";
import type { PaywallVariant } from "./paywallPersonalization";
import { SEHAJ_FALLBACK_PLANS } from "./flowData";

const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga",
  "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf"
]);

const toMajorUnits = (unitAmount: number, currency: string): number => {
  const code = currency.toLowerCase();
  return ZERO_DECIMAL_CURRENCIES.has(code) ? unitAmount : unitAmount / 100;
};

const formatUnitAmount = (unitAmount: number, currency: string): string => {
  const amount = toMajorUnits(unitAmount, currency);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      currencyDisplay: "code",
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
};

const intervalToWeeks = (interval: string, intervalCount: number): number => {
  switch (interval) {
    case "day":
      return intervalCount / 7;
    case "week":
      return intervalCount;
    case "month":
      return intervalCount * 4.33;
    case "year":
      return intervalCount * 52;
    default:
      return intervalCount * 4.33;
  }
};

const intervalToLabel = (interval: string, intervalCount: number): string => {
  if (interval === "year") {
    return intervalCount === 1 ? "1 year" : `${intervalCount} years`;
  }
  if (interval === "month") {
    if (intervalCount === 1) return "1 month";
    if (intervalCount === 12) return "1 year";
    return `${intervalCount} months`;
  }
  if (interval === "week") {
    return intervalCount === 1 ? "1 week" : `${intervalCount} weeks`;
  }
  return `${intervalCount} ${interval}`;
};

const PLAN_SAVE_PERCENT: Record<string, number> = {
  weekly: 40,
  monthly: 40,
  quarterly: 50,
  annual: 70
};

const MOST_POPULAR_PLAN = "quarterly";

const mapStripePriceToOption = (
  planId: string,
  price: StripePlanPrice,
  mostPopularPlan: string
): PaymentPlanOption => ({
  id: planId,
  label: intervalToLabel(price.interval, price.intervalCount),
  savePercent: PLAN_SAVE_PERCENT[planId] ?? 0,
  weeksInPeriod: intervalToWeeks(price.interval, price.intervalCount),
  totalPrice: formatUnitAmount(price.unitAmount, price.currency),
  currencyCode: price.currency.toUpperCase(),
  amount: toMajorUnits(price.unitAmount, price.currency),
  isMostPopular: planId === mostPopularPlan
});

export const mapPricesResponseToPlans = (
  prices: StripePricesResponse,
  paywallVariant: PaywallVariant = "control"
): PaymentPlanOption[] => [
  ...(prices.weekly ? [mapStripePriceToOption("weekly", prices.weekly, paywallVariant === "annual_trial_3d" ? "annual" : MOST_POPULAR_PLAN)] : []),
  mapStripePriceToOption("monthly", prices.monthly, paywallVariant === "annual_trial_3d" ? "annual" : MOST_POPULAR_PLAN),
  mapStripePriceToOption("quarterly", prices.quarterly, paywallVariant === "annual_trial_3d" ? "annual" : MOST_POPULAR_PLAN),
  mapStripePriceToOption("annual", prices.annual, paywallVariant === "annual_trial_3d" ? "annual" : MOST_POPULAR_PLAN)
];

export type LivePricesState = {
  plans: PaymentPlanOption[];
  isLoading: boolean;
  error: string | null;
  resolvedProvider: CheckoutProvider | null;
  resolvedCurrency: string | null;
  hasLivePrices: boolean;
};

export const resolveLiveMonthlyOfferForTracking = (
  state: LivePricesState,
  provider: CheckoutProvider
): { contentId: string; currency: string; value: number } | null => {
  if (!state.hasLivePrices || state.resolvedProvider !== provider) {
    return null;
  }

  const monthlyOffer = state.plans.find((plan) => plan.id === "monthly");
  const value = monthlyOffer?.amount;
  const currency = monthlyOffer?.currencyCode?.trim().toUpperCase();
  if (
    !monthlyOffer ||
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value <= 0 ||
    !currency ||
    !/^[A-Z]{3}$/.test(currency)
  ) {
    return null;
  }

  return { contentId: monthlyOffer.id, currency, value };
};

export const useLivePrices = (
  provider: CheckoutProvider = "polar",
  paywallVariant: PaywallVariant = "control",
  currency?: string
): LivePricesState => {
  const [state, setState] = useState<LivePricesState>({
    plans: SEHAJ_FALLBACK_PLANS,
    isLoading: true,
    error: null,
    resolvedProvider: null,
    resolvedCurrency: null,
    hasLivePrices: false
  });
  useEffect(() => {
    setState({
      plans: SEHAJ_FALLBACK_PLANS,
      isLoading: true,
      error: null,
      resolvedProvider: null,
      resolvedCurrency: null,
      hasLivePrices: false
    });

    let cancelled = false;

    void (async () => {
      try {
        const { fetchPrices } = await import("../api/checkout");
        const prices = await fetchPrices(undefined, provider, paywallVariant, currency);
        if (!cancelled) {
          setState({
            plans: mapPricesResponseToPlans(prices, paywallVariant),
            isLoading: false,
            error: null,
            resolvedProvider: provider,
            resolvedCurrency: prices.monthly.currency.toUpperCase(),
            hasLivePrices: true
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("[useLivePrices] Failed to fetch prices, using fallback", error);
          setState({
            plans: SEHAJ_FALLBACK_PLANS,
            isLoading: false,
            error: error instanceof Error ? error.message : "Failed to fetch prices",
            resolvedProvider: provider,
            resolvedCurrency: null,
            hasLivePrices: false
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paywallVariant, provider, currency]);

  return state;
};
